/**
 * The resurfacing domain's face (ADR 0014, Rule D).
 *
 * What brings something back unprompted: the people nudge, the stale-task micro-review, welcome
 * back, and the weekly reflection. Every file outside `src/resurfacing/` reaches it through this
 * module; nothing below it is a path another domain may name.
 *
 * **Explicit named exports, never `export *`.** A face is an interface, not a bag.
 *
 * **`welcome-back.tsx` is here rather than in `shell/`, against the placement table.**
 * `docs/target-structure.md:198` routes it to `shell/` and #317 claims it; `CONTEXT.md` governs and
 * gives "welcome back" its own headword under *Review and resurfacing* — "the re-entry summary at
 * the top of Today after 72 hours away". The file is that definition: a 72-hour absence threshold, a
 * 30-day long-absence branch, and two storage keys. A clock that brings something back after an
 * absence is this domain's defining shape, and `shell/` owns the provider stack, the error boundary,
 * the right panel, observability and the landing page — none of which this is. `micro-review-card`
 * also names `suppressIfWelcomeBack` inside its own type, so one surface here already coordinates
 * with it (#254).
 *
 * **This domain writes through the slices, never through `document/` directly.** A reflection is a
 * journal entry and a review acts on a task, so the four writes call `entries/journal/write-spec.ts`
 * and `entries/task/write-spec.ts`. `rg -in changedoc src/resurfacing/` returns nothing, and neither
 * does `useDocumentOps`.
 *
 * **Every hook module here opens with 'use client', and the build is what says so.**
 * `app/app/settings/page.tsx` is a server component and imports this face for `ReflectionSettings`,
 * which pulls every module the face names into the RSC graph. Without the directive `pnpm build`
 * fails with "You are importing a module that depends on `useEffect` into a React Server
 * Component module" — while `type-check`, `lint`, the suite and `check:mocks` all stay silent.
 * `document/index.ts` records the same hazard for the same reason.
 *
 * @see CONTEXT.md, *Review and resurfacing*
 */

// ============================================================================
// Surfaces
//
// `MicroReviewCard` has no mount point. It is carried rather than deleted because #84 exists to
// mount it, and it is complete — round-robin stale-task selection, the visibility rule, and the
// three actions. Deleting it would pre-empt an open decision; #314 deleted `topic-filter-panel.tsx`
// on the opposite reading, where the deciding ticket (#157) is about whether the surface ships at
// all. Its dependency `selectStaleReviews` and that module's ten-test suite come with it.
// ============================================================================

export { PeopleNudges } from './people-nudges'
export { WelcomeBack } from './welcome-back'
export { MicroReviewCard } from './micro-review-card'
export { WeeklyReflectionCard } from './weekly-reflection-card'
export { ReflectionSettings } from './reflection-settings'

// ============================================================================
// Reads
//
// `useWeeklyReflection` is what `entries/journal/view.tsx` mounts the card from. Its two
// localStorage keys are the pair `reflection-settings.tsx` writes, which is why that settings
// surface belongs to this domain rather than to `account/`.
// ============================================================================

export { useWeeklyReflection } from './weekly-reflection'
export { usePeopleNudges } from './select-people-nudges'
export type { OverdueContact } from './select-people-nudges'
